import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { GET_CITIES } from '@/core/graphql/queries/getCities';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { City } from '@/core/models/locations/city';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import Head from 'next/head';
import Link from 'next/link';
import { GetStaticPaths, GetStaticProps } from 'next';

const TRUCK_PARTS_CATEGORY_ID = '4939';

const POPULAR_TRUCK_MAKES = [
    'Iveco', 'Mercedes', 'MAN', 'Volvo', 'DAF', 'Scania', 'Hino', 'Isuzu',
    'FAW', 'Fuso', 'Actros', 'Freightliner', 'Kenworth', 'Peterbilt', 'UD Trucks',
];

const TOP_CITIES = [
    'Johannesburg', 'Cape Town', 'Durban', 'Pretoria', 'Gqeberha',
    'Bloemfontein', 'East London', 'Polokwane', 'Nelspruit',
    'Roodepoort', 'Benoni', 'Boksburg', 'Germiston', 'Randburg', 'Sandton',
];

interface Props {
    businesses: BusinessItem[];
    pagination: { count: number; currentPage: number; totalPages: number };
    make: string;
    makeDisplay: string;
    city: string;
    cityName: string;
}

export default function TruckPartsMakeCityPage({
    businesses,
    pagination,
    make,
    makeDisplay,
    city,
    cityName,
}: Props) {
    const searchParams = {
        category: TRUCK_PARTS_CATEGORY_ID,
        text: makeDisplay,
        city,
        province: '',
        suburb: '',
        page: 1,
    };

    const pageTitle = `${makeDisplay} Truck Parts in ${cityName} | Commercial Vehicle Spares`;
    const pageDescription = `Find ${makeDisplay} truck parts and commercial vehicle spares in ${cityName}. Browse trusted suppliers of ${makeDisplay} truck components, engine parts, and accessories near you.`;
    const canonicalUrl = `${ENV.DOMAIN_URL}truck-parts/${make}/${city}/`;

    const keywords = [
        `${makeDisplay} truck parts ${cityName}`,
        `${makeDisplay} commercial vehicle spares ${cityName}`,
        `${makeDisplay} truck spares South Africa`,
        `truck parts ${cityName}`,
        `commercial vehicle parts ${cityName}`,
        `heavy duty truck spares ${cityName}`,
    ].join(', ');

    const faqItems = [
        {
            question: `Where can I find ${makeDisplay} truck parts in ${cityName}?`,
            answer: `Browse our directory to find trusted suppliers of ${makeDisplay} truck spares and commercial vehicle parts in ${cityName}.`,
        },
        {
            question: `What types of ${makeDisplay} truck parts are available in ${cityName}?`,
            answer: `Suppliers in ${cityName} stock engine parts, transmission components, brakes, suspension, electrical parts, cab components, and more for ${makeDisplay} commercial vehicles.`,
        },
        {
            question: `Can I get same-day ${makeDisplay} truck parts in ${cityName}?`,
            answer: `Many suppliers in ${cityName} carry stock for popular ${makeDisplay} models. Contact suppliers directly to confirm availability and delivery options.`,
        },
    ];

    const breadcrumbs = [
        { label: 'Truck Parts', href: '/truck-parts/' },
        { label: makeDisplay, href: `/truck-parts/${make}/` },
        { label: cityName, href: `/truck-parts/${make}/${city}/` },
    ];

    const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.label,
            item: `${ENV.DOMAIN_URL}${crumb.href.replace(/^\//, '')}`,
        })),
    };

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywords} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
                />
            </Head>

            <section className="relative overflow-hidden bg-[#0f1512] text-white">
                <div
                    className="absolute inset-0 opacity-35"
                    style={{
                        backgroundImage:
                            'radial-gradient(circle at 15% 20%, rgba(220, 38, 38, 0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(239, 68, 68, 0.3), transparent 40%)',
                    }}
                />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <nav className="text-sm text-red-200 mb-3">
                                {breadcrumbs.map((crumb, i) => (
                                    <span key={crumb.href}>
                                        {i > 0 && <span className="mx-1">&gt;</span>}
                                        {i < breadcrumbs.length - 1 ? (
                                            <Link href={crumb.href} className="hover:text-white">{crumb.label}</Link>
                                        ) : (
                                            <span className="text-white">{crumb.label}</span>
                                        )}
                                    </span>
                                ))}
                            </nav>
                            <p className="text-xs uppercase tracking-[0.3em] text-red-200">Commercial Vehicle Parts</p>
                            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
                                {makeDisplay} Truck Parts in {cityName}
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-red-100">
                                Quality {makeDisplay} commercial vehicle parts and truck spares from trusted suppliers
                                in {cityName}, South Africa.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <Link
                                    href="#business-filters"
                                    className="bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Find Suppliers
                                </Link>
                                <Link
                                    href="/truck-parts/"
                                    className="border border-white/30 text-white px-5 py-2.5 rounded-md text-sm font-semibold"
                                >
                                    All Truck Parts
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="my-10" id="business-filters">
                <div className="container">
                    <BusinessList
                        searchParams={searchParams}
                        initialBusinesses={businesses}
                        initialPagination={pagination}
                    />
                </div>
            </div>

            <section className="container my-12">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <p className="text-xs uppercase tracking-[0.25em] text-red-700">Truck Parts Directory</p>
                            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                                {makeDisplay} Parts in {cityName}
                            </h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Find local suppliers of {makeDisplay} truck and commercial vehicle parts in {cityName}.
                                Browse new and used spares from trusted automotive businesses.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {['Engine Parts', 'Transmission', 'Brakes', 'Suspension', 'Electrical', 'Cab Parts'].map((tag) => (
                                    <span
                                        key={tag}
                                        className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">Frequently Asked Questions</h2>
                            <div className="mt-4 space-y-4">
                                {faqItems.map((item) => (
                                    <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                        <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                        <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <DownloadApp />
        </>
    );
}

export const getStaticPaths: GetStaticPaths = async () => {
    const apolloClient = initializeApollo();

    const { data: cityData } = await apolloClient.query({ query: GET_CITIES });
    const cities: City[] = cityData?.getCity?.result || [];

    const includedCities = cities
        .filter((c: City) => c.include === true || c.include === 1)
        .filter((c: City) => TOP_CITIES.includes(c.cityName));

    const paths: { params: { make: string; city: string } }[] = [];

    for (const make of POPULAR_TRUCK_MAKES) {
        for (const city of includedCities) {
            paths.push({ params: { make: slugify(make), city: slugify(city.cityName) } });
        }
    }

    return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    const apolloClient = initializeApollo();

    const makeSlug = params?.make as string;
    const citySlug = params?.city as string;

    const { data: cityData } = await apolloClient.query({ query: GET_CITIES });
    const cities: City[] = cityData?.getCity?.result || [];

    const city = cities.find((c: City) => slugify(c.cityName) === citySlug);
    if (!city) return { notFound: true };

    const makeDisplay = makeSlug
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

    const businessRequest: SearchBusinessRequest = {
        statusIds: '1',
        domainId: Number(ENV.CATEGORY_ID),
        categoryIds: TRUCK_PARTS_CATEGORY_ID,
        provinceIds: '',
        cityIds: String(city.cityId),
        suburbIds: '',
        companyName: '',
        page: 1,
        size: 12,
    };

    const { data: businessData } = await apolloClient.query({
        query: GET_BUSINESS_LIST,
        variables: businessRequest,
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';

    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: {
                count: businessData?.getBusinessList?.count ?? 0,
                currentPage: businessData?.getBusinessList?.currentPage ?? 1,
                totalPages: businessData?.getBusinessList?.totalPages ?? 0,
            },
            make: makeSlug,
            makeDisplay,
            city: citySlug,
            cityName: city.cityName,
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
